import { styled, Theme, MenuList } from '@mui/material';

import ActionButton from '@components/ActionButton';
import { Container } from '@components/Layout/components/Container';
import { CoreBox, CoreMenuItem, CoreTypography } from '@youscience/core';

// TODO add colors to library
const lightGreyBg = '#F9F9F9';
const sidebarBorderColor = '#7FC8CB';

export const linkOffset = (offset: number) =>
  ({
    '::before': {
      content: '""',
      display: 'block',
      height: `${offset}px`,
      marginTop: `-${offset}px`,
      visibility: 'hidden',
    },
  }) as const;

export const sxStyles = {
  headSectionBg: (theme: Theme) => ({
    backgroundColor: theme.palette.common.white,
    overflow: 'hidden',
  }),
  mainSectionBg: {
    backgroundColor: lightGreyBg,
  },
  mainSectionContentWrap: {
    maxWidth: '940px',
    width: '100%',
  },
  aboutSectionWrap: {
    maxWidth: '640px',
  },
  aboutSectionTitle: (theme: Theme) => ({
    color: theme.palette.primary.main,
    marginBottom: '1.5rem',
  }),
  showMoreButton: (theme: Theme) => ({
    color: theme.palette.primary.main,
    textDecoration: 'underline',

    '&:hover': {
      cursor: 'pointer',
    },
  }),
  areasOfStudySectionTitle: (theme: Theme) => ({
    marginTop: '4rem',
    marginBottom: '2.5rem',
    color: theme.palette.primary.main,
  }),
  utahBadgeWrap: {
    marginTop: '0.75rem',
  },
  linkOffset,
};

export const StyledHeadSectionInner = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'end',
  maxWidth: '1320px',
  margin: '0 auto',
  position: 'relative',

  [theme.breakpoints.down('lg')]: {
    flexDirection: 'column',
    justifyContent: 'center',
  },
}));

export const StyledHeadSectionInfo = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  maxWidth: '634px',
  width: '100%',
  padding: '1.25rem',
}));

export const StyledHeadSectionTitle = styled(CoreTypography)(({ theme }) => ({
  marginTop: '3.5rem',

  [theme.breakpoints.down('xs')]: {
    marginTop: '2rem',
  },
}));

export const StyledHeadSectionActions = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'wrap',
  marginTop: '3rem',
  gap: '0.5rem',

  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    marginTop: '2.75rem',
  },
}));

export const StyledWebsiteButton = styled(ActionButton)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    width: '100%',
  },
}));

export const StyledSchoolPhotoWrap = styled('div')(({ theme }) => ({
  maxWidth: '850px',
  width: '100%',
  margin: '1.5rem auto 0 auto',

  img: {
    position: 'static',
    display: 'block',
    margin: '0 auto',
    borderBottomLeftRadius: '240px',
    maxHeight: '480px',
    height: '100%',
  },

  [theme.breakpoints.up('lg')]: {
    width: '850px',
    margin: '0',

    img: {
      position: 'relative',
      left: '74px',
    },
  },

  [theme.breakpoints.up('xl')]: {
    img: {
      position: 'relative',
      left: '256px',
    },
  },
}));

export const StyledMainSectionInner = styled(Container)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: '6rem',
  padding: '4rem 1.25rem',

  [theme.breakpoints.down('sm')]: {
    marginTop: '4rem',
  },
}));

export const StyledSidebarWrap = styled('div')(({ theme }) => ({
  maxWidth: '360px',
  width: '100%',
  height: 'auto',
  overflow: 'visible',
  visibility: 'visible',

  [theme.breakpoints.down('md')]: {
    maxWidth: '64px',
    marginRight: '1.5rem',
  },

  [theme.breakpoints.down('sm')]: {
    width: '1px',
    height: '1px',
    overflow: 'hidden',
    visibility: 'hidden',
    marginRight: 0,
  },
}));

export const SectionLinkOffset = styled(CoreBox)(() => ({
  ...sxStyles.linkOffset(120),
}));

export const StyledSidebarInner = styled(MenuList)(({ theme }) => ({
  position: 'sticky',
  top: '100px',

  'li.Mui-selected': {
    border: `1px solid ${sidebarBorderColor}`,
    borderRadius: '4px',
    backgroundColor: theme.palette.common.white,
    color: theme.palette.primary.main,

    '&:hover': {
      cursor: 'default',
      backgroundColor: theme.palette.common.white,
    },
  },
}));

export const StyledSidebarItemInner = styled(CoreMenuItem)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  maxWidth: '294px',
  marginBottom: '0.5rem',
  padding: '0.75rem 1rem',
  border: `1px solid ${lightGreyBg}`,
  color: theme.palette.common.black,

  '& > span:first-of-type': {
    display: 'none',
  },

  // fixed gutter so labels align regardless of each icon's intrinsic svg width
  '& > div:first-of-type': {
    display: 'flex',
    justifyContent: 'center',
    flexShrink: 0,
    width: '26px',
    marginRight: '1rem',
  },

  '&:hover': {
    backgroundColor: lightGreyBg,
    color: theme.palette.primary.main,
    border: `1px solid ${sidebarBorderColor}`,
    borderRadius: '4px',
    transition: 'border 0.3s color 0.3s',
  },

  [theme.breakpoints.down('md')]: {
    minHeight: '50px',

    '& > div:first-of-type': {
      margin: 0,
    },
  },
}));

export const StyledSidebarItemTitle = styled(CoreTypography)(({ theme }) => ({
  width: '100%',
  height: 'auto',
  overflow: 'visible',
  visibility: 'visible',

  [theme.breakpoints.down('md')]: {
    width: '1px',
    height: '1px',
    overflow: 'hidden',
    visibility: 'hidden',
  },
}));
